import { motion } from "framer-motion";
import { Page, PageHero } from "../components/Layout";
import { ShieldCheck, MessageSquare, Users2, Building2, CheckCircle2 } from "lucide-react";
import { FadeIn, Stagger, StaggerItem } from "../components/motion";

const TESTIMONIALS = [
  {
    company: "United Precast Concrete",
    location: "Doha, Qatar",
    text: "Vardhamaan has been our premier manpower sourcing partner for major infrastructure and precast projects. Their stringent pre-screening processes and transparent logistics handling ensure we receive highly skilled personnel exactly on schedule.",
    author: "Director of Operations",
    category: "Infrastructure"
  },
  {
    company: "Leminar Global",
    location: "Dubai, UAE",
    text: "Working with Vardhamaan to scale our MEP division was seamless. Sourcing certified HVAC technicians and mechanical engineers is always challenging, but their deep candidate network across South Asia delivered stellar results.",
    author: "Head of Human Resources",
    category: "MEP & Engineering"
  },
  {
    company: "Global Mechanical Engineering Co. W.L.L.",
    location: "Kuwait",
    text: "Over the course of our 5-year relationship, Vardhamaan has successfully mobilized and onboarded over 600 skilled workers for our heavy engineering projects. Their commitment to compliance and welfare is exemplary.",
    author: "Executive Vice President",
    category: "Mechanical"
  },
  {
    company: "Rayneau Group of Companies",
    location: "Caribbean Region",
    text: "For our large-scale civil construction contracts, Vardhamaan mobilized top-tier heavy equipment operators, surveyors, and site leads. Their team managed the entire visa and deployment pipeline flawlessly.",
    author: "Chief Executive Officer",
    category: "Civil Construction"
  }
];

const DEPLOYMENTS = [
  {
    title: "Metro Expansion Project",
    location: "Doha, Qatar",
    workers: "450+ Mobilized",
    roles: "Steel Fixers, Carpenters, Operators",
    status: "Completed & Active",
    image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=600&q=80"
  },
  {
    title: "District Cooling Plant Sourcing",
    location: "Abu Dhabi, UAE",
    workers: "180+ Mobilized",
    roles: "HVAC Technicians, Welders, QA/QC Engineers",
    status: "Ongoing Operation",
    image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=600&q=80"
  },
  {
    title: "International Airport Terminal 3",
    location: "Jeddah, Saudi Arabia",
    workers: "800+ Mobilized",
    roles: "MEP Technicians, Electrical Specialists, Riggers",
    status: "Delivered",
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=600&q=80"
  }
];

export default function Partners() {
  return (
    <Page testId="partners-page">
      <PageHero
        image="/assets/bg-services.jpg"
        eyebrow="Our Trusted Network"
        title={<>Collaborating with global leaders <span className="text-[#D4AF37]">to power progress.</span></>}
        sub="Over the last two decades, Vardhamaan has served as the preferred workforce mobilization partner for heavy industry, civil infrastructure, engineering, and logistics conglomerates."
      />

      {/* Trusted Partners Section */}
      <section className="bg-white py-20" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="font-mono text-xs text-[#811F5D] tracking-[0.2em] uppercase font-bold inline-flex items-center gap-1.5 mb-3">
              <Building2 size={14} /> Global Enterprise Clients
            </span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#2A0E20] tracking-tight">
              Approved Sourcing Partner for Leading Conglomerates
            </h2>
            <p className="mt-4 text-[#2A0E20]/75 leading-relaxed text-base sm:text-lg">
              We recruit, train, and deploy certified talent for highly demanding operational projects. Here are the leading enterprises that trust us.
            </p>
          </div>

          {/* Client Logos Display */}
          <div className="relative group max-w-4xl mx-auto">
            {/* Outer glowing border */}
            <div className="absolute -inset-1.5 bg-gradient-to-r from-[#811F5D]/20 via-[#D4AF37]/30 to-[#811F5D]/20 rounded-3xl blur opacity-30 group-hover:opacity-60 transition duration-1000 group-hover:duration-200" />
            
            {/* Main Card */}
            <div className="relative overflow-hidden rounded-2xl border border-[#811F5D]/10 bg-white p-4 sm:p-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-1 flex flex-col items-center">
              <h3 className="font-heading font-bold text-xs text-[#811F5D] uppercase tracking-[0.2em] mb-6">
                Our Active Global Client Panel
              </h3>
              <div className="overflow-hidden rounded-xl border border-white/50 bg-[#FAF7F2] w-full">
                <img
                  src="/assets/clients-grid.png"
                  alt="Vardhamaan partners and client logos including United Precast Concrete, Leminar, Global, Rayneau, Leminar Global, Carisun, Technical Experts, TTE, etc."
                  className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.01]"
                  data-testid="partners-logo-grid"
                />
              </div>
              <div className="mt-5 flex items-center justify-center gap-2 text-xs font-semibold text-[#2A0E20]/60">
                <ShieldCheck size={16} className="text-[#D4AF37]" />
                <span>Registered, Audited & Compliant Workforce Mobilization</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Grid Section */}
      <section className="bg-[#FAF7F2] py-20 border-y border-[#811F5D]/10" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-4">
            <div>
              <span className="font-mono text-xs text-[#811F5D] tracking-[0.2em] uppercase font-bold inline-flex items-center gap-1.5 mb-3">
                <MessageSquare size={14} /> Client Testimonials
              </span>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#2A0E20] tracking-tight">
                What Our Partners Say About Us
              </h2>
            </div>
            <p className="max-w-md text-[#2A0E20]/75 text-sm sm:text-base leading-relaxed">
              Don't just take our word for it. Read reviews from leaders in construction, engineering, and energy projects.
            </p>
          </div>

          <Stagger className="grid sm:grid-cols-2 gap-8">
            {TESTIMONIALS.map((t, idx) => (
              <StaggerItem
                key={idx}
                className="bg-white border border-[#811F5D]/15 p-8 rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 relative group hover:-translate-y-1.5 flex flex-col justify-between"
                data-testid={`testimonial-card-${idx}`}
              >
                <div>
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <span className="inline-block bg-[#FAF7F2] text-[#811F5D] border border-[#811F5D]/15 px-3 py-1 text-[11px] uppercase font-mono tracking-wider font-bold rounded-lg mb-2">
                        {t.category}
                      </span>
                      <h3 className="font-heading font-extrabold text-xl text-[#2A0E20]">
                        {t.company}
                      </h3>
                      <p className="text-xs text-[#2A0E20]/60 mt-0.5 font-medium">{t.location}</p>
                    </div>
                    <span className="text-5xl font-serif text-[#D4AF37]/25 select-none absolute top-4 right-6 group-hover:text-[#D4AF37]/45 transition-colors">“</span>
                  </div>
                  <p className="text-[#2A0E20]/80 text-sm leading-relaxed mb-6 relative z-10 italic">
                    "{t.text}"
                  </p>
                </div>
                <div className="border-t border-[#811F5D]/10 pt-4 flex items-center justify-between">
                  <p className="font-heading font-bold text-xs text-[#811F5D]">{t.author}</p>
                  <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded-md">
                    <CheckCircle2 size={12} /> Verified Client
                  </span>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Workforce & Deployment Gallery Section */}
      <section className="bg-white py-20" data-testid="deployments-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="font-mono text-xs text-[#811F5D] tracking-[0.2em] uppercase font-bold inline-flex items-center gap-1.5 mb-3">
              <Users2 size={14} className="text-[#D4AF37]" /> Deployment In Action
            </span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#2A0E20] tracking-tight">
              Featured Workforce Deployments
            </h2>
            <p className="mt-4 text-[#2A0E20]/75 leading-relaxed text-base">
              A glimpse at major infrastructure and industrial sites powered by professionals, operators, and staff mobilized by Vardhamaan.
            </p>
          </div>

          <Stagger className="grid md:grid-cols-3 gap-8">
            {DEPLOYMENTS.map((d, idx) => (
              <StaggerItem
                key={idx}
                className="overflow-hidden border border-[#811F5D]/15 bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 group hover:-translate-y-1.5 flex flex-col justify-between"
                data-testid={`deployment-card-${idx}`}
              >
                <div>
                  <div className="h-52 overflow-hidden relative">
                    <img
                      src={d.image}
                      alt={d.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-60" />
                    <div className="absolute top-4 right-4 bg-[#811F5D] text-white text-[11px] font-bold tracking-wider uppercase px-3 py-1 rounded-full shadow-lg border border-white/20">
                      {d.workers}
                    </div>
                  </div>
                  <div className="p-6">
                    <span className="font-mono text-[11px] font-bold text-[#D4AF37] uppercase tracking-wide">
                      {d.location}
                    </span>
                    <h3 className="font-heading font-extrabold text-lg text-[#2A0E20] mt-1 group-hover:text-[#811F5D] transition-colors">
                      {d.title}
                    </h3>
                    <p className="mt-2.5 text-xs text-[#2A0E20]/75 leading-relaxed font-medium">
                      <span className="text-[#811F5D] font-semibold">Key Roles Sourced:</span> {d.roles}
                    </p>
                  </div>
                </div>
                <div className="px-6 pb-6 pt-2">
                  <div className="pt-4 border-t border-[#811F5D]/10 flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600">
                      <CheckCircle2 size={14} /> {d.status}
                    </span>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </Page>
  );
}
